import { createContext, type ReactNode, useContext, useMemo } from "react";
import {
  type ExplorerSection,
  type ExplorerUiState,
  useExplorerSections,
  useExplorerUiState,
  useStoredIdPruning,
} from "./explorerData";

// The Explorer's tree and UI state, shared between the Explorer panel and
// the AI Helper (which reads the tree for its snapshot and drives
// expand/collapse/filter). One instance per Studio page, mounted whether
// or not the panel is on screen (a view-only studio hides the panel).

export type ExplorerValue = ExplorerUiState & {
  sections: ExplorerSection[];
  servicesLoading: boolean;
  hasServices: boolean;
};

const ExplorerContext = createContext<ExplorerValue | null>(null);

export function ExplorerProvider({ children }: { children: ReactNode }) {
  const ui = useExplorerUiState();
  const { sections, servicesLoading, hasServices } = useExplorerSections();
  // Pruning must see every section — the panel's tab filter is
  // render-only, so switching tabs never wipes stored expand state.
  useStoredIdPruning(sections, !servicesLoading, ui);
  const value = useMemo(
    () => ({ ...ui, sections, servicesLoading, hasServices }),
    [ui, sections, servicesLoading, hasServices],
  );
  return (
    <ExplorerContext.Provider value={value}>
      {children}
    </ExplorerContext.Provider>
  );
}

export function useExplorer(): ExplorerValue {
  const value = useContext(ExplorerContext);
  if (!value) throw new Error("useExplorer requires ExplorerProvider");
  return value;
}
